/**
 * Fallback icon and label per import type code. The backend sends displayName and
 * icon for each type, which take precedence; this map covers responses without them
 * and places that only have the code, such as the upload history filter.
 */
export const IMPORT_TYPE_CONFIG = {
    'IOT': {
        icon: 'iot-connection-icon',
        label: 'IOT Onboarding'
    },
    'INVENTORY': {
        icon: 'vehicle-bus-icon',
        label: 'Inventory Onboarding'
    },
    'INVENTORY_NEW': {
        icon: 'vehicle-bus-icon',
        label: 'New Inventory Onboarding'
    },
    'INVENTORY_UPDATE': {
        icon: 'inventory-update-icon',
        label: 'Update Existing Inventory'
    },
    'INVENTORY_REPLACE_IOT': {
        icon: 'inventory-replace-icon',
        label: 'Replace IoT Device in Vehicles'
    }
};

/**
 * Map an import type code to its display icon and label. Known types use
 * IMPORT_TYPE_CONFIG; unknown types fall back to a title-cased label.
 */
export const getImportTypeConfig = (type) => {
    if (IMPORT_TYPE_CONFIG[type]) return IMPORT_TYPE_CONFIG[type];

    return {
        icon: 'vehicle-bus-icon',
        label: type
            ? `${type.charAt(0).toUpperCase()}${type.slice(1).toLowerCase()} Onboarding`
            : 'Onboarding'
    };
};

/**
 * Filter raw API import types to the ones the user may access, and format them
 * for display.
 *
 * A type is accessible when it is ungated (no controlledByFeatureCode) or the
 * user has the `create` permission on its controlling feature. This mirrors the
 * backend filtering and acts as defense in depth against a stale/shared
 * response. `hasPermission` returns true for RBAC v1 orgs (legacy fallback), so
 * v1 orgs see every type.
 *
 * @param {Array} rawTypes - import types from GET /api/dataimports/types
 * @param {(featureCode: string, operation: string) => boolean} hasPermission
 * @returns {Array} formatted, access-filtered import types
 */
export const getAccessibleImportTypes = (rawTypes, hasPermission) =>
    (rawTypes || [])
        .filter(item =>
            !item.controlledByFeatureCode ||
            hasPermission(item.controlledByFeatureCode, 'create')
        )
        .map(item => {
            const config = getImportTypeConfig(item.type);
            // Prefer the backend-provided displayName/icon; fall back to the
            // local map so older API responses (without these fields) still work.
            return {
                id: item.id,
                type: item.type,
                label: item.displayName || config.label,
                icon: item.icon || config.icon,
                controlledByFeatureCode: item.controlledByFeatureCode,
                sampleCSVTemplateS3Path: item.sampleCSVTemplateS3Path
            };
        });
